import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@byscott-io/corebyscott";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { api } from "../lib/api";

const fmtDate = (d) => d.toISOString().slice(0, 10);
const num = (n) => (n ?? 0).toLocaleString();

function StatCard({ title, value, sub, accent }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <p className="text-sm text-gray-500">{title}</p>
      <p className={`text-2xl font-semibold mt-1 ${accent || "text-gray-900"}`}>{value}</p>
      {sub && <p className="text-sm text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function TierBadge({ tier }) {
  const styles = {
    identified: "bg-green-100 text-green-800",
    anonymous: "bg-gray-100 text-gray-600",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${styles[tier] || styles.anonymous}`}>
      {tier}
    </span>
  );
}

// Channel keys come from config/acquisition_channels.yml; an unknown one falls
// back to the neutral style rather than disappearing.
const CHANNEL_STYLES = {
  ai_answer: "bg-purple-100 text-purple-800",
  search: "bg-blue-100 text-blue-800",
  social: "bg-pink-100 text-pink-800",
  internal: "bg-gray-100 text-gray-600",
  referral: "bg-amber-100 text-amber-800",
  direct: "bg-slate-100 text-slate-700",
};

const CHANNEL_BARS = {
  ai_answer: "bg-purple-500",
  search: "bg-blue-500",
  social: "bg-pink-500",
  internal: "bg-gray-400",
  referral: "bg-amber-500",
  direct: "bg-slate-400",
};

function ChannelBadge({ channel, label }) {
  if (!channel) return <span className="text-gray-400">—</span>;
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${CHANNEL_STYLES[channel] || CHANNEL_STYLES.direct}`}>
      {label || channel.replace(/_/g, " ")}
    </span>
  );
}

// Acquisition channel of the active set. The server returns every channel in a
// fixed order with zeros included, so an empty `ai_answer` reads as "none this
// range" rather than vanishing.
function ChannelBreakdown({ rows }) {
  const total = rows.reduce((sum, r) => sum + (r.visitors || 0), 0);
  const ai = rows.find((r) => r.channel === "ai_answer");

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Acquisition channel</h2>
      <p className="text-sm text-gray-500 mb-4">Where active visitors came from, by referrer.</p>
      {total === 0 ? (
        <p className="text-gray-500 text-center py-6">No referrer data in this range.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const pct = total ? Math.round((r.visitors / total) * 100) : 0;
            return (
              <div key={r.channel} className="flex items-center gap-3 text-sm">
                <div className="w-44 shrink-0"><ChannelBadge channel={r.channel} label={r.label} /></div>
                <div className="flex-1 bg-gray-100 rounded h-2 overflow-hidden">
                  <div className={`h-2 ${CHANNEL_BARS[r.channel] || CHANNEL_BARS.direct}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="w-24 text-right text-gray-700 tabular-nums">
                  {num(r.visitors)} <span className="text-gray-400">({pct}%)</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
      {ai && ai.visitors > 0 && (
        <p className="text-xs text-gray-500 mt-4">
          AI answer engine counts <strong>click-throughs</strong>, not citations. An answer that quotes
          this site without the reader clicking leaves no trace at all, so treat this as a floor on AI
          visibility rather than a measure of it.
        </p>
      )}
    </div>
  );
}

const shortId = (id) => (id ? `${id.slice(0, 8)}…` : "—");
const shortTime = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

function VisitorDetail({ appId, visitorId, onClose }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["visitor", appId, visitorId],
    queryFn: () => api.getVisitor(visitorId, { app_id: appId }),
    enabled: !!appId && !!visitorId,
  });

  return (
    <div className="bg-white rounded-lg shadow p-6 border-l-4 border-indigo-500">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Visitor detail</h2>
          <p className="font-mono text-xs text-gray-500 break-all">{visitorId}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-sm">Close ✕</button>
      </div>

      {error && <p className="text-red-600 mt-3">{error.message}</p>}
      {isLoading ? (
        <p className="text-gray-500 mt-4">Loading…</p>
      ) : data ? (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><p className="text-gray-500">Tier</p><TierBadge tier={data.tier} /></div>
          <div><p className="text-gray-500">Channel</p><ChannelBadge channel={data.channel} /></div>
          <div><p className="text-gray-500">First seen</p><p className="text-gray-900">{shortTime(data.first_seen_at)}</p></div>
          <div><p className="text-gray-500">Last seen</p><p className="text-gray-900">{shortTime(data.last_seen_at)}</p></div>
          <div><p className="text-gray-500">Events (lifetime)</p><p className="text-gray-900">{num(data.visit_count)}</p></div>
          {data.account && (
            <>
              <div><p className="text-gray-500">Account (container)</p><p className="text-gray-900">{data.account.container_id ?? "—"}</p></div>
              <div><p className="text-gray-500">User</p><p className="text-gray-900">{data.account.user_id ?? "—"}</p></div>
            </>
          )}
          {data.credited_code && (
            <div><p className="text-gray-500">Referral code</p><p className="font-mono text-gray-900">{data.credited_code}</p></div>
          )}
          <div className="col-span-2 md:col-span-4">
            <p className="text-gray-500 mb-1">Attribution</p>
            <pre className="bg-gray-50 rounded p-3 text-xs overflow-x-auto text-gray-700">
              {JSON.stringify(data.attribution || {}, null, 2)}
            </pre>
          </div>

          <div className="col-span-2 md:col-span-4">
            <p className="text-gray-500 mb-1">
              Events {data.events?.length ? `(${data.events.length} most recent)` : ""}
            </p>
            {!data.events || data.events.length === 0 ? (
              <p className="text-gray-400 text-xs">No events recorded.</p>
            ) : (
              <div className="max-h-64 overflow-y-auto border border-gray-100 rounded">
                <table className="min-w-full text-xs">
                  <tbody className="divide-y divide-gray-100">
                    {data.events.map((e, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-1.5 font-medium text-gray-700 whitespace-nowrap">{e.event}</td>
                        <td className="px-3 py-1.5 text-gray-600 truncate max-w-xs" title={e.label || ""}>{e.label || "—"}</td>
                        <td className="px-3 py-1.5 text-gray-400 whitespace-nowrap text-right">
                          {e.occurred_at ? new Date(e.occurred_at).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function Visitors() {
  const { currentContainer } = useAuth();
  const [appId, setAppId] = useState("");
  const [dateFrom, setDateFrom] = useState(fmtDate(new Date(Date.now() - 30 * 86400000)));
  const [dateTo, setDateTo] = useState(fmtDate(new Date()));
  const [page, setPage] = useState(1);
  const [selectedVisitor, setSelectedVisitor] = useState(null);

  const { data: appsData } = useQuery({
    queryKey: ["apps", currentContainer?.id],
    queryFn: api.getApps,
    enabled: !!currentContainer,
  });
  const apps = Array.isArray(appsData) ? appsData : (appsData?.data || appsData?.apps || []);

  // Default to the first app once the list loads.
  useEffect(() => {
    if (!appId && apps.length > 0) setAppId(String(apps[0].id));
  }, [apps, appId]);

  // Reset paging + open detail when the query changes.
  useEffect(() => { setPage(1); setSelectedVisitor(null); }, [appId, dateFrom, dateTo]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["visitors", currentContainer?.id, appId, dateFrom, dateTo, page],
    queryFn: () => api.getVisitors({ app_id: appId, date_from: dateFrom, date_to: dateTo, page }),
    enabled: !!currentContainer && !!appId,
    keepPreviousData: true,
  });

  if (!currentContainer) {
    return <div className="text-center py-12"><p className="text-gray-500">Loading account…</p></div>;
  }

  const totals = data?.totals || { active: 0, new: 0, returning: 0, identified: 0, anonymous: 0, with_attribution: 0 };
  const series = data?.by_day || [];
  const channels = data?.by_channel || [];
  const rows = data?.data || [];
  const meta = data?.meta || { page: 1, last_page: 1, total: 0, from: 0, to: 0 };
  const identifiedPct = totals.active ? Math.round((totals.identified / totals.active) * 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Visitors</h1>
        <p className="text-gray-500">
          Website visitors over time, per app — new vs returning, and known accounts vs anonymous.
        </p>
      </div>

      {/* Controls: app + date range */}
      <div className="flex flex-wrap gap-4 items-end bg-white rounded-lg shadow p-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">App</label>
          <select
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 min-w-[12rem]"
          >
            {apps.length === 0 && <option value="">No apps</option>}
            {apps.map((a) => <option key={a.id} value={a.id}>{a.name || a.slug}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
          <input type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
          <input type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2" />
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error.message}</div>}

      {isLoading && !data ? (
        <div className="animate-pulse grid grid-cols-1 md:grid-cols-5 gap-6">
          {[1, 2, 3, 4, 5].map((i) => <div key={i} className="bg-gray-200 rounded-lg h-28" />)}
        </div>
      ) : (
        <>
          {/* Activity + tier cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard title="Active visitors" value={num(totals.active)} sub="≥1 visit in range" />
            <StatCard title="New" value={num(totals.new)} sub="First-ever visit in range" accent="text-indigo-600" />
            <StatCard title="Returning" value={num(totals.returning)} sub="Seen before this range" accent="text-emerald-600" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard title="Identified" value={num(totals.identified)} sub={`${identifiedPct}% of active — known account`} accent="text-green-700" />
            <StatCard title="Anonymous" value={num(totals.anonymous)} sub="No account yet" />
            <StatCard title="With attribution" value={num(totals.with_attribution)} sub="Has a referral/UTM source" />
          </div>

          {/* Billing-gap note */}
          {data && !data.billing_available && totals.identified > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded text-sm">
              Prospect vs. customer split isn’t shown here yet — that needs billing state, which isn’t wired for this app.
              Identified visitors are known accounts; their paying status is unavailable.
            </div>
          )}

          {/* Acquisition channel (#732) */}
          <ChannelBreakdown rows={channels} />

          {/* Time series */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity over time</h2>
            <div className="h-64">
              {series.every((d) => d.active === 0) ? (
                <p className="text-gray-500 text-center py-8">No visitor activity in this range.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="new" stackId="1" stroke="#6366f1" fill="#6366f1" fillOpacity={0.5} />
                    <Area type="monotone" dataKey="returning" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.5} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Drill-down detail */}
          {selectedVisitor && (
            <VisitorDetail appId={appId} visitorId={selectedVisitor} onClose={() => setSelectedVisitor(null)} />
          )}

          {/* Visitor table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Active visitors</h2>
              <span className="text-sm text-gray-500">{num(meta.total)} total</span>
            </div>
            {rows.length === 0 ? (
              <p className="text-gray-500 text-center py-10">No visitors in this range.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {["Visitor", "Tier", "New?", "Channel", "Source", "Account", "Events", "Last active"].map((h) => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rows.map((r) => (
                      <tr
                        key={r.visitor_id}
                        data-track="visitors.open-detail"
                        onClick={() => setSelectedVisitor(r.visitor_id)}
                        className={`hover:bg-indigo-50 cursor-pointer ${selectedVisitor === r.visitor_id ? "bg-indigo-50" : ""}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap font-mono text-gray-700">{shortId(r.visitor_id)}</td>
                        <td className="px-6 py-4 whitespace-nowrap"><TierBadge tier={r.tier} /></td>
                        <td className="px-6 py-4 whitespace-nowrap">{r.new ? "New" : "Returning"}</td>
                        <td className="px-6 py-4 whitespace-nowrap"><ChannelBadge channel={r.channel} /></td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">{r.source || "—"}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">{r.account?.container_id ?? "—"}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">{num(r.events_in_window)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">{shortTime(r.last_active_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Pagination */}
            {meta.last_page > 1 && (
              <div className="px-6 py-3 border-t flex items-center justify-between text-sm">
                <span className="text-gray-500">Showing {meta.from}–{meta.to} of {num(meta.total)}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={meta.page <= 1}
                    className="px-3 py-1 border rounded disabled:opacity-40"
                  >Prev</button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={meta.page >= meta.last_page}
                    className="px-3 py-1 border rounded disabled:opacity-40"
                  >Next</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
